import type { SupportedLocale } from '@erp/shared';

const de = {
  messages: {
    title: 'E-Mails',
    description: 'Nachrichten aus allen für dich freigegebenen Postfächern und Ordnern.',
    newest: 'Neueste zuerst',
    oldest: 'Älteste zuerst',
    subjectAsc: 'Betreff A–Z',
    subjectDesc: 'Betreff Z–A',
    pageLabel: 'Seite {{page}} von {{pages}} · {{total}} Nachrichten',
    search: 'Nachrichten durchsuchen',
    searchScopeHint:
      'Durchsucht standardmäßig alle zugänglichen Ordner. Nur Inhalte und Quellen, die du lesen darfst, können Treffer liefern.',
    allAccounts: 'Alle freigegebenen Postfächer',
    account: 'Postfach',
    subject: 'Betreff',
    from: 'Von',
    to: 'An',
    cc: 'CC',
    date: 'Datum',
    noSubject: 'Ohne Betreff',
    unknownDate: 'Datum unbekannt',
    empty: 'Noch keine lesbaren Nachrichten vorhanden.',
    noResults: 'Keine passenden Nachrichten.',
    loadFailed: 'Die E-Mails konnten nicht geladen werden.',
    detailFailed: 'Diese Nachricht ist nicht verfügbar oder nicht mehr freigegeben.',
    retry: 'Erneut versuchen',
    loading: 'Nachricht wird geladen',
    message: 'Nachricht',
    thread: 'Gesprächsverlauf',
    noThread: 'Keine weiteren freigegebenen Nachrichten in diesem Gespräch.',
    threadLimited: 'Der Verlauf zeigt die ersten 100 freigegebenen Nachrichten.',
    sources: 'Quellkopien',
    sourceHint:
      'Jede Quellkopie hat eigene Rechte. Eine Aktenverknüpfung gibt keine weiteren Gesprächsnachrichten frei.',
    textOnly: 'Sichere Textansicht. Externe Bilder und aktive Inhalte werden nicht geladen.',
    noBody: 'Kein darstellbarer Nachrichtentext vorhanden.',
    attachments: 'Anhänge',
    downloadPart: '{{filename}} herunterladen',
    original: 'Original herunterladen',
    downloadFailed: 'Die Datei konnte nicht heruntergeladen werden. Bitte erneut versuchen.',
    blocked: 'Dieser Anhang kann nicht separat heruntergeladen werden.',
    bccUnknown: 'BCC unbekannt',
    bccObserved: 'BCC aus verfügbaren Originaldaten; Vollständigkeit unbekannt',
    bccComplete: 'Bekannter tatsächlicher BCC-Umschlag',
    bccInvalid: 'Einige BCC-Angaben konnten nicht als E-Mail-Adresse gelesen werden.',
    historical: 'Früherer Postfachstand',
    missing: 'Am ursprünglichen Fundort nicht mehr vorhanden',
    draft: 'Entwurf',
    junk: 'Spam/Junk',
    evidence: 'Ordner und Herkunft',
    findings: 'Hinweise',
    folder_INBOX: 'Posteingang',
    folder_SENT: 'Gesendet',
    folder_ARCHIVE: 'Archiv',
    folder_DRAFTS: 'Entwürfe',
    folder_JUNK: 'Spam/Junk',
    folder_TRASH: 'Papierkorb',
    folder_ALL: 'Sammelordner',
    folder_CUSTOM: 'Eigener Ordner',
    folder_UNKNOWN: 'Ordnerfunktion unbekannt',
    direction_INCOMING: 'Eingegangen',
    direction_OUTGOING: 'Ausgehend',
    direction_UNKNOWN: 'Richtung unklar',
    finding_IDENTITY_INSUFFICIENT:
      'Keine verlässliche Kennung; mögliche Kopien werden vorsichtig getrennt aufbewahrt.',
    finding_DATE_INVALID_OR_MISSING: 'Das Nachrichtendatum fehlt oder ist ungültig.',
    finding_ADDRESS_INVALID: 'Einige sichtbare Adressen sind ungültig.',
    finding_SENDER_MULTIPLE_OR_MISSING: 'Absender fehlt oder ist nicht eindeutig.',
    finding_HEADER_DUPLICATE: 'Widersprüchliche oder mehrfache Nachrichtenköpfe prüfen.',
    finding_ATTACHMENT_BLOCKED: 'Mindestens ein Anhang ist für den separaten Download gesperrt.',
    finding_BODY_TRUNCATED:
      'Die Textansicht wurde wegen ihrer Größe gekürzt; das Original bleibt erhalten.',
    finding_BODY_CONVERSION_FAILED:
      'Der HTML-Inhalt konnte nicht vollständig in sicheren Text umgewandelt werden.',
    finding_MESSAGE_ID_COLLISION:
      'Diese Nachrichtenkennung wurde im selben Postfachstand mit anderem Inhalt gefunden.',
    finding_MESSAGE_ID_INVALID: 'Die Nachrichtenkennung ist ungültig.',
    finding_PROVIDER_ID_COLLISION:
      'Die Providerkennung verweist im selben Postfachstand auf unterschiedliche Inhalte.',
  },
  errors: {
    sourceInvalid:
      'Die Nachricht ist ungültig, beschädigt oder überschreitet die zulässigen Grenzen.',
    immutableSource:
      'Gespeicherte Mailoriginale und Nachrichteninhalte können nicht überschrieben werden.',
    invalidSourceEvidence: 'Die Herkunft der Maildatei konnte nicht bestätigt werden.',
    invalidBccEvidence: 'Die BCC-Herkunft ist widersprüchlich.',
    privateSource: 'Maildateien sind ausschließlich über berechtigte Nachrichten erreichbar.',
    generationConflict:
      'Das Postfach wurde geändert oder deaktiviert. Bitte den Abruf neu starten.',
    locationContentChanged:
      'Der Server liefert unter derselben Ordnerkennung einen anderen Originalinhalt. Eine Prüfung ist erforderlich.',
  },
  validation: {
    bccEvidence: 'BCC-Adressen und Herkunft müssen zu den tatsächlich bekannten Daten passen.',
  },
};
type MailMessageTexts = {
  messages: { [K in keyof typeof de.messages]: string };
  errors: { [K in keyof typeof de.errors]: string };
  validation: { [K in keyof typeof de.validation]: string };
};

export const centralMailMessageTranslations = {
  de,
  en: {
    messages: {
      title: 'Emails',
      description: 'Messages from every mailbox and folder you are allowed to read.',
      newest: 'Newest first',
      oldest: 'Oldest first',
      subjectAsc: 'Subject A–Z',
      subjectDesc: 'Subject Z–A',
      pageLabel: 'Page {{page}} of {{pages}} · {{total}} messages',
      search: 'Search messages',
      searchScopeHint:
        'By default, searches all accessible folders. Only content and sources you can read can produce matches.',
      allAccounts: 'All permitted mailboxes',
      account: 'Mailbox',
      subject: 'Subject',
      from: 'From',
      to: 'To',
      cc: 'CC',
      date: 'Date',
      noSubject: 'No subject',
      unknownDate: 'Unknown date',
      empty: 'No readable messages yet.',
      noResults: 'No matching messages.',
      loadFailed: 'Emails could not be loaded.',
      detailFailed: 'This message is unavailable or no longer shared with you.',
      retry: 'Try again',
      loading: 'Loading message',
      message: 'Message',
      thread: 'Conversation',
      noThread: 'No other permitted messages in this conversation.',
      threadLimited: 'The conversation shows the first 100 permitted messages.',
      sources: 'Source copies',
      sourceHint:
        'Each source copy has separate permissions. A record link does not share other conversation messages.',
      textOnly: 'Safe text view. External images and active content are not loaded.',
      noBody: 'No displayable message text.',
      attachments: 'Attachments',
      downloadPart: 'Download {{filename}}',
      original: 'Download original',
      downloadFailed: 'The file could not be downloaded. Please try again.',
      blocked: 'This attachment cannot be downloaded separately.',
      bccUnknown: 'BCC unknown',
      bccObserved: 'BCC from available source data; completeness unknown',
      bccComplete: 'Known actual BCC envelope',
      bccInvalid: 'Some BCC entries could not be read as email addresses.',
      historical: 'Previous mailbox identity',
      missing: 'No longer present at its original location',
      draft: 'Draft',
      junk: 'Spam/Junk',
      evidence: 'Folders and provenance',
      findings: 'Findings',
      folder_INBOX: 'Inbox',
      folder_SENT: 'Sent',
      folder_ARCHIVE: 'Archive',
      folder_DRAFTS: 'Drafts',
      folder_JUNK: 'Spam/Junk',
      folder_TRASH: 'Trash',
      folder_ALL: 'Aggregate folder',
      folder_CUSTOM: 'Custom folder',
      folder_UNKNOWN: 'Unknown folder function',
      direction_INCOMING: 'Incoming',
      direction_OUTGOING: 'Outgoing',
      direction_UNKNOWN: 'Unclear direction',
      finding_IDENTITY_INSUFFICIENT:
        'No reliable identifier; possible copies are conservatively retained separately.',
      finding_DATE_INVALID_OR_MISSING: 'The message date is missing or invalid.',
      finding_ADDRESS_INVALID: 'Some visible addresses are invalid.',
      finding_SENDER_MULTIPLE_OR_MISSING: 'The sender is missing or ambiguous.',
      finding_HEADER_DUPLICATE: 'Check duplicate or conflicting message headers.',
      finding_ATTACHMENT_BLOCKED: 'At least one attachment is blocked from separate download.',
      finding_BODY_TRUNCATED: 'The text view was shortened due to size; the original is retained.',
      finding_BODY_CONVERSION_FAILED: 'The HTML content could not be fully converted to safe text.',
      finding_MESSAGE_ID_COLLISION:
        'This message identifier has different content within the same mailbox identity.',
      finding_MESSAGE_ID_INVALID: 'The message identifier is invalid.',
      finding_PROVIDER_ID_COLLISION:
        'The provider identifier has different content within the same mailbox identity.',
    },
    errors: {
      sourceInvalid: 'The message is invalid, damaged or exceeds supported limits.',
      immutableSource: 'Stored mail originals and message contents cannot be overwritten.',
      invalidSourceEvidence: 'The mail file provenance could not be verified.',
      invalidBccEvidence: 'BCC provenance is inconsistent.',
      privateSource: 'Mail files are accessible only through authorized messages.',
      generationConflict: 'The mailbox changed or was disabled. Start synchronization again.',
      locationContentChanged:
        'The server returned different original bytes for the same folder identifier. Review is required.',
    },
    validation: {
      bccEvidence: 'BCC addresses and provenance must reflect the actually known data.',
    },
  },
  fr: {
    messages: {
      title: 'E-mails',
      description:
        'Messages de toutes les boîtes et de tous les dossiers que vous pouvez consulter.',
      newest: 'Plus récents d’abord',
      oldest: 'Plus anciens d’abord',
      subjectAsc: 'Objet A–Z',
      subjectDesc: 'Objet Z–A',
      pageLabel: 'Page {{page}} sur {{pages}} · {{total}} messages',
      search: 'Rechercher des messages',
      searchScopeHint:
        'Par défaut, tous les dossiers accessibles sont parcourus. Seuls les contenus et sources que vous pouvez lire produisent des résultats.',
      allAccounts: 'Toutes les boîtes autorisées',
      account: 'Boîte mail',
      subject: 'Objet',
      from: 'De',
      to: 'À',
      cc: 'CC',
      date: 'Date',
      noSubject: 'Sans objet',
      unknownDate: 'Date inconnue',
      empty: 'Aucun message consultable pour le moment.',
      noResults: 'Aucun message correspondant.',
      loadFailed: 'Impossible de charger les e-mails.',
      detailFailed: 'Ce message est indisponible ou ne vous est plus partagé.',
      retry: 'Réessayer',
      loading: 'Chargement du message',
      message: 'Message',
      thread: 'Conversation',
      noThread: 'Aucun autre message autorisé dans cette conversation.',
      threadLimited: 'La conversation affiche les 100 premiers messages autorisés.',
      sources: 'Copies sources',
      sourceHint:
        'Chaque copie possède ses propres droits. Un lien dans un dossier ne partage pas les autres messages de la conversation.',
      textOnly:
        'Affichage texte sécurisé. Les images externes et les contenus actifs ne sont pas chargés.',
      noBody: 'Aucun texte de message affichable.',
      attachments: 'Pièces jointes',
      downloadPart: 'Télécharger {{filename}}',
      original: 'Télécharger l’original',
      downloadFailed: 'Impossible de télécharger le fichier. Veuillez réessayer.',
      blocked: 'Cette pièce jointe ne peut pas être téléchargée séparément.',
      bccUnknown: 'CCI inconnu',
      bccObserved: 'CCI provenant des sources disponibles ; exhaustivité inconnue',
      bccComplete: 'Enveloppe CCI réelle connue',
      bccInvalid: 'Certaines mentions CCI ne sont pas des adresses e-mail lisibles.',
      historical: 'Ancienne identité de boîte',
      missing: 'Absent de son emplacement d’origine',
      draft: 'Brouillon',
      junk: 'Indésirables',
      evidence: 'Dossiers et provenance',
      findings: 'Points à vérifier',
      folder_INBOX: 'Réception',
      folder_SENT: 'Envoyés',
      folder_ARCHIVE: 'Archives',
      folder_DRAFTS: 'Brouillons',
      folder_JUNK: 'Indésirables',
      folder_TRASH: 'Corbeille',
      folder_ALL: 'Dossier regroupé',
      folder_CUSTOM: 'Dossier personnalisé',
      folder_UNKNOWN: 'Fonction du dossier inconnue',
      direction_INCOMING: 'Entrant',
      direction_OUTGOING: 'Sortant',
      direction_UNKNOWN: 'Sens indéterminé',
      finding_IDENTITY_INSUFFICIENT:
        'Identifiant non fiable ; les copies possibles sont conservées séparément par précaution.',
      finding_DATE_INVALID_OR_MISSING: 'La date du message est absente ou incorrecte.',
      finding_ADDRESS_INVALID: 'Certaines adresses visibles sont incorrectes.',
      finding_SENDER_MULTIPLE_OR_MISSING: 'L’expéditeur est absent ou ambigu.',
      finding_HEADER_DUPLICATE: 'Vérifiez les en-têtes répétés ou contradictoires.',
      finding_ATTACHMENT_BLOCKED:
        'Au moins une pièce jointe est bloquée pour le téléchargement séparé.',
      finding_BODY_TRUNCATED:
        'L’affichage texte a été raccourci en raison de sa taille ; l’original est conservé.',
      finding_BODY_CONVERSION_FAILED:
        'Le contenu HTML n’a pas pu être entièrement converti en texte sécurisé.',
      finding_MESSAGE_ID_COLLISION:
        'Cet identifiant de message correspond à des contenus différents dans la même identité de boîte.',
      finding_MESSAGE_ID_INVALID: 'L’identifiant du message est incorrect.',
      finding_PROVIDER_ID_COLLISION:
        'L’identifiant du fournisseur correspond à des contenus différents dans la même identité de boîte.',
    },
    errors: {
      sourceInvalid: 'Le message est incorrect, endommagé ou dépasse les limites prises en charge.',
      immutableSource:
        'Les originaux et les contenus des messages enregistrés ne peuvent pas être écrasés.',
      invalidSourceEvidence: 'La provenance du fichier mail n’a pas pu être vérifiée.',
      invalidBccEvidence: 'La provenance CCI est contradictoire.',
      privateSource: 'Les fichiers mail sont accessibles uniquement par les messages autorisés.',
      generationConflict: 'La boîte a changé ou a été désactivée. Relancez la synchronisation.',
      locationContentChanged:
        'Le serveur fournit un original différent pour le même identifiant de dossier. Une vérification est nécessaire.',
    },
    validation: {
      bccEvidence:
        'Les adresses CCI et leur provenance doivent correspondre aux données réellement connues.',
    },
  },
  es: {
    messages: {
      title: 'Correos',
      description: 'Mensajes de todos los buzones y carpetas que puedes consultar.',
      newest: 'Más recientes primero',
      oldest: 'Más antiguos primero',
      subjectAsc: 'Asunto A–Z',
      subjectDesc: 'Asunto Z–A',
      pageLabel: 'Página {{page}} de {{pages}} · {{total}} mensajes',
      search: 'Buscar mensajes',
      searchScopeHint:
        'Por defecto se buscan todas las carpetas accesibles. Solo coinciden el contenido y las fuentes que puedes leer.',
      allAccounts: 'Todos los buzones autorizados',
      account: 'Buzón',
      subject: 'Asunto',
      from: 'De',
      to: 'Para',
      cc: 'CC',
      date: 'Fecha',
      noSubject: 'Sin asunto',
      unknownDate: 'Fecha desconocida',
      empty: 'Aún no hay mensajes que puedas consultar.',
      noResults: 'No hay mensajes coincidentes.',
      loadFailed: 'No se pudieron cargar los correos.',
      detailFailed: 'Este mensaje no está disponible o ya no está compartido contigo.',
      retry: 'Reintentar',
      loading: 'Cargando mensaje',
      message: 'Mensaje',
      thread: 'Conversación',
      noThread: 'No hay más mensajes autorizados en esta conversación.',
      threadLimited: 'La conversación muestra los primeros 100 mensajes autorizados.',
      sources: 'Copias de origen',
      sourceHint:
        'Cada copia tiene sus propios permisos. Un enlace en una ficha no comparte los demás mensajes de la conversación.',
      textOnly: 'Vista de texto segura. No se cargan imágenes externas ni contenido activo.',
      noBody: 'No hay texto del mensaje que se pueda mostrar.',
      attachments: 'Adjuntos',
      downloadPart: 'Descargar {{filename}}',
      original: 'Descargar original',
      downloadFailed: 'No se pudo descargar el archivo. Vuelve a intentarlo.',
      blocked: 'Este adjunto no se puede descargar por separado.',
      bccUnknown: 'CCO desconocido',
      bccObserved: 'CCO de los datos de origen disponibles; no se conoce su integridad',
      bccComplete: 'Destinatarios CCO reales conocidos',
      bccInvalid: 'Algunas entradas CCO no se pudieron interpretar como direcciones de correo.',
      historical: 'Identidad anterior del buzón',
      missing: 'Ya no está en su ubicación original',
      draft: 'Borrador',
      junk: 'Spam',
      evidence: 'Carpetas y procedencia',
      findings: 'Avisos',
      folder_INBOX: 'Entrada',
      folder_SENT: 'Enviados',
      folder_ARCHIVE: 'Archivo',
      folder_DRAFTS: 'Borradores',
      folder_JUNK: 'Spam',
      folder_TRASH: 'Papelera',
      folder_ALL: 'Carpeta agregada',
      folder_CUSTOM: 'Carpeta personalizada',
      folder_UNKNOWN: 'Función de carpeta desconocida',
      direction_INCOMING: 'Entrante',
      direction_OUTGOING: 'Saliente',
      direction_UNKNOWN: 'Dirección incierta',
      finding_IDENTITY_INSUFFICIENT:
        'No hay identificador fiable; las posibles copias se conservan por separado por precaución.',
      finding_DATE_INVALID_OR_MISSING: 'La fecha del mensaje falta o no es válida.',
      finding_ADDRESS_INVALID: 'Algunas direcciones visibles no son válidas.',
      finding_SENDER_MULTIPLE_OR_MISSING: 'El remitente falta o es ambiguo.',
      finding_HEADER_DUPLICATE: 'Revisa las cabeceras repetidas o contradictorias.',
      finding_ATTACHMENT_BLOCKED: 'Al menos un adjunto está bloqueado para descarga separada.',
      finding_BODY_TRUNCATED:
        'La vista de texto se ha acortado por su tamaño; se conserva el original.',
      finding_BODY_CONVERSION_FAILED: 'No se pudo convertir todo el HTML en texto seguro.',
      finding_MESSAGE_ID_COLLISION:
        'Este identificador de mensaje tiene contenidos distintos dentro de la misma identidad del buzón.',
      finding_MESSAGE_ID_INVALID: 'El identificador del mensaje no es válido.',
      finding_PROVIDER_ID_COLLISION:
        'El identificador del proveedor tiene contenidos distintos dentro de la misma identidad del buzón.',
    },
    errors: {
      sourceInvalid: 'El mensaje no es válido, está dañado o supera los límites admitidos.',
      immutableSource: 'Los originales y contenidos de correo guardados no se pueden sobrescribir.',
      invalidSourceEvidence: 'No se pudo verificar la procedencia del archivo de correo.',
      invalidBccEvidence: 'La procedencia CCO es contradictoria.',
      privateSource: 'Los archivos de correo solo son accesibles desde mensajes autorizados.',
      generationConflict: 'El buzón ha cambiado o se ha desactivado. Reinicia la sincronización.',
      locationContentChanged:
        'El servidor ha devuelto otro original para el mismo identificador de carpeta. Es necesario revisarlo.',
    },
    validation: {
      bccEvidence:
        'Las direcciones CCO y su procedencia deben reflejar los datos realmente conocidos.',
    },
  },
  tr: {
    messages: {
      title: 'E-postalar',
      description: 'Okuma izniniz olan tüm posta kutuları ve klasörlerdeki iletiler.',
      newest: 'Önce en yeniler',
      oldest: 'Önce en eskiler',
      subjectAsc: 'Konu A–Z',
      subjectDesc: 'Konu Z–A',
      pageLabel: 'Sayfa {{page}} / {{pages}} · {{total}} ileti',
      search: 'İletilerde ara',
      searchScopeHint:
        'Varsayılan olarak erişilebilir tüm klasörler aranır. Yalnızca okuyabildiğiniz içerikler ve kaynaklar eşleşir.',
      allAccounts: 'İzin verilen tüm posta kutuları',
      account: 'Posta kutusu',
      subject: 'Konu',
      from: 'Kimden',
      to: 'Kime',
      cc: 'Bilgi',
      date: 'Tarih',
      noSubject: 'Konusuz',
      unknownDate: 'Tarih bilinmiyor',
      empty: 'Henüz okunabilir ileti yok.',
      noResults: 'Eşleşen ileti yok.',
      loadFailed: 'E-postalar yüklenemedi.',
      detailFailed: 'Bu ileti mevcut değil veya artık sizinle paylaşılmıyor.',
      retry: 'Yeniden dene',
      loading: 'İleti yükleniyor',
      message: 'İleti',
      thread: 'Yazışma',
      noThread: 'Bu yazışmada izin verilen başka ileti yok.',
      threadLimited: 'Yazışmada izin verilen ilk 100 ileti gösteriliyor.',
      sources: 'Kaynak kopyalar',
      sourceHint:
        'Her kaynak kopyanın ayrı izinleri vardır. Bir kayıt bağlantısı yazışmadaki diğer iletileri paylaşmaz.',
      textOnly: 'Güvenli metin görünümü. Harici resimler ve etkin içerik yüklenmez.',
      noBody: 'Gösterilebilir ileti metni yok.',
      attachments: 'Ekler',
      downloadPart: '{{filename}} indir',
      original: 'Aslını indir',
      downloadFailed: 'Dosya indirilemedi. Lütfen yeniden deneyin.',
      blocked: 'Bu ek ayrı olarak indirilemez.',
      bccUnknown: 'Gizli alıcılar bilinmiyor',
      bccObserved: 'Mevcut kaynak verilerinden gizli alıcılar; tamlığı bilinmiyor',
      bccComplete: 'Bilinen gerçek gizli alıcı zarfı',
      bccInvalid: 'Bazı gizli alıcı girdileri e-posta adresi olarak okunamadı.',
      historical: 'Önceki posta kutusu kimliği',
      missing: 'Artık ilk konumunda bulunmuyor',
      draft: 'Taslak',
      junk: 'İstenmeyen',
      evidence: 'Klasörler ve kaynak',
      findings: 'Uyarılar',
      folder_INBOX: 'Gelen kutusu',
      folder_SENT: 'Gönderilenler',
      folder_ARCHIVE: 'Arşiv',
      folder_DRAFTS: 'Taslaklar',
      folder_JUNK: 'İstenmeyen',
      folder_TRASH: 'Çöp kutusu',
      folder_ALL: 'Toplu klasör',
      folder_CUSTOM: 'Özel klasör',
      folder_UNKNOWN: 'Klasör işlevi bilinmiyor',
      direction_INCOMING: 'Gelen',
      direction_OUTGOING: 'Giden',
      direction_UNKNOWN: 'Yön belirsiz',
      finding_IDENTITY_INSUFFICIENT:
        'Güvenilir kimlik yok; olası kopyalar tedbir olarak ayrı tutulur.',
      finding_DATE_INVALID_OR_MISSING: 'İleti tarihi eksik veya geçersiz.',
      finding_ADDRESS_INVALID: 'Bazı görünür adresler geçersiz.',
      finding_SENDER_MULTIPLE_OR_MISSING: 'Gönderen eksik veya belirsiz.',
      finding_HEADER_DUPLICATE: 'Yinelenen veya çelişkili ileti başlıklarını kontrol edin.',
      finding_ATTACHMENT_BLOCKED: 'En az bir ekin ayrı indirilmesi engellendi.',
      finding_BODY_TRUNCATED: 'Metin görünümü boyutu nedeniyle kısaltıldı; aslı saklanır.',
      finding_BODY_CONVERSION_FAILED: 'HTML içeriğinin tamamı güvenli metne dönüştürülemedi.',
      finding_MESSAGE_ID_COLLISION:
        'Bu ileti kimliği aynı posta kutusu kimliğinde farklı içeriklerle bulundu.',
      finding_MESSAGE_ID_INVALID: 'İleti kimliği geçersiz.',
      finding_PROVIDER_ID_COLLISION:
        'Sağlayıcı kimliği aynı posta kutusu kimliğinde farklı içeriklerle bulundu.',
    },
    errors: {
      sourceInvalid: 'İleti geçersiz, hasarlı veya desteklenen sınırları aşıyor.',
      immutableSource: 'Kaydedilen e-posta asılları ve ileti içerikleri üzerine yazılamaz.',
      invalidSourceEvidence: 'E-posta dosyasının kaynağı doğrulanamadı.',
      invalidBccEvidence: 'Gizli alıcı kaynağı tutarsız.',
      privateSource: 'E-posta dosyalarına yalnızca yetkili iletiler üzerinden erişilir.',
      generationConflict:
        'Posta kutusu değişti veya devre dışı bırakıldı. Senkronizasyonu yeniden başlatın.',
      locationContentChanged:
        'Sunucu aynı klasör kimliği için farklı bir asıl içerik döndürdü. İnceleme gerekiyor.',
    },
    validation: {
      bccEvidence: 'Gizli alıcı adresleri ve kaynakları gerçekten bilinen verileri yansıtmalıdır.',
    },
  },
} satisfies Record<SupportedLocale, MailMessageTexts>;
